import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useContext, useEffect, useState } from "react";
import io, { Socket } from "socket.io-client";

import { API_MODE, ChatContext } from "@/contexts/chat";
import { isAiyoConnectErrorAtom } from "@/states/chat/isAiyoConnectError";
import { videoProgressAtom } from "@/states/chat/videoProgressAtom";
import { winActiveAtom } from "@/states/winActiveAtom";
import type { AllRooms } from "@/types/common/chat/context/allRooms";
import type { Metas } from "@/types/common/chat/context/metas";
import type { Venues } from "@/types/common/chat/context/venues";
import { isMobile } from "@/utils/deviceJudgment";
import type { DefaultEventsMap } from "@socket.io/component-emitter";
import { AuthContext } from "contexts/auth";
import { debugLog } from "utils/debugLog";

//チャットのWS接続制御
export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const [winActive, setWinActive] = useAtom(winActiveAtom);
  const setIsAiyoConnectError = useSetAtom(isAiyoConnectErrorAtom);
  const videoProgress = useAtomValue(videoProgressAtom);
  const [socket, setSocket] = useState<
    Socket<DefaultEventsMap, DefaultEventsMap> | undefined
  >();
  const [allRooms, setAllRooms] = useState<AllRooms[]>([]);
  const [metas, setMetas] = useState<Metas[]>([]);
  const [venues, setVenues] = useState<Venues[]>([]);
  const { userId, userType } = useContext(AuthContext);
  const [apiMode, setApiMode] = useState(API_MODE.WS);
  const [isConnected, setIsConnected] = useState(false);

  const connect = () => {
    const url = process.env.WSCHAT_URL as string;
    const newSocket = io(url, {
      transports: ["websocket", "polling"],
      auth: {
        userId,
        token: localStorage.getItem("token"),
        userType: userType?.toLowerCase()
      }
    });
    if (!userId || !userType) return;
    newSocket.on("connect", () => {
      debugLog("socket connect");
      setIsConnected(true);
      setIsAiyoConnectError(false);
    });
    newSocket.on("connect_error", () => {
      debugLog("socket connect error");
      setIsConnected(false);
      setIsAiyoConnectError(true);
    });
    newSocket.on("disconnect", (reason) => {
      debugLog("socket disconnect");
      debugLog(reason);
      if (reason === "io server disconnect") {
        // server initiated disconnection,reconnect manually
        newSocket.connect();
        setIsConnected(true);
        setIsAiyoConnectError(false);
      } else {
        setIsConnected(false);
        setIsAiyoConnectError(true);
      }
    });
    newSocket.on("error", () => {
      debugLog("socket error");
      setIsConnected(false);
      setIsAiyoConnectError(true);
    });
    setSocket(newSocket);
  };

  // WS接続
  useEffect(() => {
    // 必要データが揃っていれば接続
    if (userId && userType) {
      if (socket) socket.disconnect();
      connect();
    }
  }, [userId, userType]);

  // 切断と再接続の制御
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    // 動画送信中は切断しない（スマホの場合はその間に画面最小化しても切断されないので注意）
    if (socket && !winActive && isConnected && !videoProgress) {
      if (!isMobile()) {
        // PC => 5分で切断
        timer = setTimeout(() => {
          socket.disconnect();
        }, 300000);
      } else {
        // Mobile => 即切断
        socket.disconnect();
      }
    } else if (socket && winActive && !isConnected) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      socket.connect();
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [socket, winActive, isConnected, videoProgress]);

  //ウィンドウのアクティブ監視
  useEffect(() => {
    const onFocus = () => setWinActive(true);
    const onBlur = () => setWinActive(false);
    const onVisibilitychange = () => {
      setWinActive(document.visibilityState === "visible");
    };

    window.addEventListener("focus", onFocus);
    if (isMobile()) {
      document.addEventListener("visibilitychange", onVisibilitychange);
    } else {
      window.addEventListener("blur", onBlur);
    }

    return () => {
      window.removeEventListener("focus", onFocus);
      if (isMobile()) {
        document.removeEventListener("visibilitychange", onVisibilitychange);
      } else {
        window.removeEventListener("blur", onBlur);
      }
    };
  }, []);

  const context = {
    socket,
    isConnected,
    allRooms,
    setAllRooms,
    metas,
    setMetas,
    venues,
    setVenues,
    apiMode,
    setApiMode
  };

  return (
    <ChatContext.Provider value={context}>{children}</ChatContext.Provider>
  );
};
